import { z } from 'zod';

export const sourcingLineSchema = z.object({
  lineId: z.string(),
  itemId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  category: z.string(),
  quantity: z.number(),
  unit: z.string(),
  specifications: z.string().optional(),
});

export const sourcingEventSchema = z.object({
  eventId: z.string(),
  eventNumber: z.string(),
  title: z.string(),
  description: z.string().optional(),
  // Event type: reverse_auction (live bidding) or sealed_proposal (hidden bids)
  eventType: z.enum(['reverse_auction', 'sealed_proposal']),
  status: z.enum(['draft', 'scheduled', 'open', 'evaluation', 'awarded', 'cancelled']),
  deliveryZone: z.string(),
  deliverySiteId: z.string(),
  deliverySiteName: z.string(),
  requiredDeliveryDate: z.string(),
  // For sealed proposals: deadline to submit
  bidDeadline: z.string(),
  // For reverse auctions: start and end time of live bidding
  auctionStartTime: z.string().optional(),
  auctionEndTime: z.string().optional(),
  // Current lowest bid (visible in reverse auctions)
  currentLowestBid: z.number().optional(),
  currentLowestBidder: z.string().optional(),
  // Starting/reserve price for reverse auctions
  startingPrice: z.number().optional(),
  reservePrice: z.number().optional(),
  lines: z.array(sourcingLineSchema),
  estimatedValue: z.number(),
  currency: z.string(),
  totalBids: z.number().optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SourcingEvent = z.infer<typeof sourcingEventSchema>;
export type SourcingLine = z.infer<typeof sourcingLineSchema>;
export type SourcingStatus = SourcingEvent['status'];
export type SourcingEventType = SourcingEvent['eventType'];

export const sourcingEventsResponseSchema = z.object({
  data: z.array(sourcingEventSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
});

export type SourcingEventsResponse = z.infer<typeof sourcingEventsResponseSchema>;

// Bid types
export const bidLineSchema = z.object({
  lineId: z.string(),
  itemId: z.string(),
  unitPrice: z.number(),
  totalPrice: z.number(),
  leadTimeDays: z.number(),
  notes: z.string().optional(),
});

export const bidSchema = z.object({
  bidId: z.string(),
  eventId: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  status: z.enum(['draft', 'submitted', 'under_review', 'accepted', 'rejected', 'withdrawn', 'outbid']),
  lines: z.array(bidLineSchema),
  subtotal: z.number(),
  tax: z.number(),
  total: z.number(),
  currency: z.string(),
  validUntil: z.string(),
  notes: z.string().optional(),
  // For reverse auctions - bid rank
  rank: z.number().optional(),
  isLowestBid: z.boolean().optional(),
  submittedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Bid = z.infer<typeof bidSchema>;
export type BidLine = z.infer<typeof bidLineSchema>;
export type BidStatus = Bid['status'];

export const bidsResponseSchema = z.object({
  data: z.array(bidSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
});

export type BidsResponse = z.infer<typeof bidsResponseSchema>;

export const SOURCING_STATUS_LABELS: Record<SourcingStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  open: 'Open',
  evaluation: 'Under Evaluation',
  awarded: 'Awarded',
  cancelled: 'Cancelled',
};

export const SOURCING_STATUS_COLORS: Record<SourcingStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  open: 'bg-green-100 text-green-800',
  evaluation: 'bg-amber-100 text-amber-800',
  awarded: 'bg-purple-100 text-purple-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const EVENT_TYPE_LABELS: Record<SourcingEventType, string> = {
  reverse_auction: 'Reverse Auction',
  sealed_proposal: 'Sealed Proposal',
};

export const EVENT_TYPE_COLORS: Record<SourcingEventType, string> = {
  reverse_auction: 'bg-orange-100 text-orange-800',
  sealed_proposal: 'bg-indigo-100 text-indigo-800',
};

export const BID_STATUS_LABELS: Record<BidStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  outbid: 'Outbid',
};

export const BID_STATUS_COLORS: Record<BidStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-100 text-gray-800',
  outbid: 'bg-orange-100 text-orange-800',
};
